import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  TextField,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  FormControlLabel,
  Checkbox,
  Collapse
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { Add as AddIcon, Remove as RemoveIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { format, parseISO } from 'date-fns';
import { uk } from 'date-fns/locale';
import { bookingAPI, inventoryAPI } from '../services/api';

// Custom function to format date with nominative case and capitalized month name
const formatDateWithMonth = (date) => {
  // Use 'LLL' for standalone abbreviated month name (nominative case)
  const formattedDate = format(date, 'd LLL yyyy HH:mm', { locale: uk });
  // Split the string to capitalize the month name
  const parts = formattedDate.split(' ');
  if (parts.length >= 2) {
    parts[1] = parts[1].charAt(0).toUpperCase() + parts[1].slice(1);
  }
  return parts.join(' ');
};

const UserBookings = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [bookingToDelete, setBookingToDelete] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  // Edit booking states
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [bookingToEdit, setBookingToEdit] = useState(null);
  const [editFormData, setEditFormData] = useState({
    pickupDatetime: new Date(),
    returnDatetime: new Date(),
    purpose: '',
    additionalNotes: '',
    alternativePickupPerson: '',
    inventoryItems: []
  });
  const [alternativePickup, setAlternativePickup] = useState(false);
  const [availableItems, setAvailableItems] = useState([]);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState('');
  const [selectedQuantity, setSelectedQuantity] = useState(1);
  const [itemDetails, setItemDetails] = useState({});
  const [editLoading, setEditLoading] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    fetchUserBookings();

    // Check for success message from location state (e.g., after creating a booking)
    if (location.state?.message) {
      setSnackbarMessage(location.state.message);
      setSnackbarOpen(true);
      // Clear the location state
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  const fetchUserBookings = async () => {
    setLoading(true);
    try {
      const response = await bookingAPI.getCurrentUserBookings();
      setBookings(response.data);
      setError('');
    } catch (err) {
      setError('Не вдалося завантажити ваші бронювання. Будь ласка, спробуйте пізніше.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (booking) => {
    setBookingToDelete(booking);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!bookingToDelete) return;

    console.log('[DEBUG_LOG] Deleting booking with ID:', bookingToDelete.id);
    try {
      console.log('[DEBUG_LOG] Calling bookingAPI.deleteBooking');
      const response = await bookingAPI.deleteBooking(bookingToDelete.id);
      console.log('[DEBUG_LOG] Delete booking response:', response);
      setBookings(bookings.filter(b => b.id !== bookingToDelete.id));
      setSnackbarMessage('Бронювання успішно видалено');
      setSnackbarOpen(true);
      console.log('[DEBUG_LOG] Booking deleted successfully');
    } catch (err) {
      console.error('[DEBUG_LOG] Error deleting booking:', err);
      if (err.response) {
        console.error('[DEBUG_LOG] Error response:', err.response.status, err.response.data);
      }
      setError('Не вдалося видалити бронювання. Будь ласка, спробуйте ще раз.');
    } finally {
      setDeleteDialogOpen(false);
      setBookingToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setBookingToDelete(null);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  // Edit booking functions
  const fetchAvailableItems = useCallback(async () => {
    if (!editFormData.pickupDatetime || !editFormData.returnDatetime) return;

    setItemsLoading(true);
    try {
      const response = await inventoryAPI.getAvailableItems(
        format(editFormData.pickupDatetime, "yyyy-MM-dd'T'HH:mm:ss"),
        format(editFormData.returnDatetime, "yyyy-MM-dd'T'HH:mm:ss")
      );
      setAvailableItems(response.data);
    } catch (err) {
      console.error('Failed to fetch available items', err);
      setError('Не вдалося завантажити доступні предмети. Будь ласка, спробуйте ще раз.');
    } finally {
      setItemsLoading(false);
    }
  }, [editFormData.pickupDatetime, editFormData.returnDatetime]);

  const handleEditClick = (booking) => {
    setBookingToEdit(booking);

    // Convert string dates to Date objects
    const pickupDate = parseISO(booking.pickupDatetime);
    const returnDate = parseISO(booking.returnDatetime);

    // Map inventory items to the format expected by the form
    const inventoryItems = booking.inventoryItems.map(item => ({
      inventoryItemId: item.inventoryItemId,
      quantity: item.quantity
    }));

    // Initialize item details
    const details = {};
    booking.inventoryItems.forEach(item => {
      details[item.inventoryItemId] = {
        id: item.inventoryItemId,
        name: item.inventoryItemName
      };
    });

    setItemDetails(details);

    setEditFormData({
      pickupDatetime: pickupDate,
      returnDatetime: returnDate,
      purpose: booking.purpose,
      additionalNotes: booking.additionalNotes || '',
      alternativePickupPerson: booking.alternativePickupPerson || '',
      inventoryItems: inventoryItems
    });

    // Set alternativePickup checkbox state based on whether there's an alternativePickupPerson
    setAlternativePickup(!!booking.alternativePickupPerson);

    setEditDialogOpen(true);

    // Fetch available items for the selected time range
    fetchAvailableItems();
  };

  const handleEditCancel = () => {
    setEditDialogOpen(false);
    setBookingToEdit(null);
    setEditFormData({
      pickupDatetime: new Date(),
      returnDatetime: new Date(),
      purpose: '',
      additionalNotes: '',
      alternativePickupPerson: '',
      inventoryItems: []
    });
    setAlternativePickup(false);
    setSelectedItemId('');
    setSelectedQuantity(1);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAlternativePickupChange = (e) => {
    setAlternativePickup(e.target.checked);
    if (!e.target.checked) {
      // Clear the alternativePickupPerson field when unchecked
      setEditFormData(prev => ({
        ...prev,
        alternativePickupPerson: ''
      }));
    }
  };

  const handleDateChange = (name, value) => {
    setEditFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAddItem = () => {
    if (!selectedItemId || selectedQuantity < 1) return;

    // Check if item is already in the list
    const existingItemIndex = editFormData.inventoryItems.findIndex(
      item => item.inventoryItemId === parseInt(selectedItemId)
    );

    if (existingItemIndex >= 0) {
      // Update quantity if item already exists
      const updatedItems = [...editFormData.inventoryItems];
      updatedItems[existingItemIndex].quantity += parseInt(selectedQuantity);

      setEditFormData(prev => ({
        ...prev,
        inventoryItems: updatedItems
      }));
    } else {
      // Add new item
      setEditFormData(prev => ({
        ...prev,
        inventoryItems: [
          ...prev.inventoryItems,
          {
            inventoryItemId: parseInt(selectedItemId),
            quantity: parseInt(selectedQuantity)
          }
        ]
      }));

      // Fetch item details if not already in state
      if (!itemDetails[selectedItemId]) {
        inventoryAPI.getItemById(selectedItemId)
          .then(response => {
            setItemDetails(prev => ({
              ...prev,
              [selectedItemId]: response.data
            }));
          })
          .catch(err => console.error(`Failed to fetch details for item ${selectedItemId}`, err));
      }
    }

    // Reset selection
    setSelectedItemId('');
    setSelectedQuantity(1);
  };

  const handleRemoveItem = (index) => {
    const updatedItems = [...editFormData.inventoryItems];
    updatedItems.splice(index, 1);

    setEditFormData(prev => ({
      ...prev,
      inventoryItems: updatedItems
    }));
  };

  const handleIncreaseQuantity = (index) => {
    const updatedItems = [...editFormData.inventoryItems];
    const item = updatedItems[index];
    const inventoryItemId = item.inventoryItemId;

    // Find the available item to check maximum quantity
    const availableItem = availableItems.find(ai => ai.id === inventoryItemId);

    if (availableItem) {
      // Calculate how many more can be added
      const currentlyBooked = editFormData.inventoryItems.reduce((total, i) => {
        if (i.inventoryItemId === inventoryItemId) {
          return total + i.quantity;
        }
        return total;
      }, 0);

      // Only increase if not exceeding available quantity
      if (currentlyBooked < availableItem.availableQuantity) {
        updatedItems[index].quantity += 1;

        setEditFormData(prev => ({
          ...prev,
          inventoryItems: updatedItems
        }));
      }
    }
  };

  const handleDecreaseQuantity = (index) => {
    const updatedItems = [...editFormData.inventoryItems];

    // Only decrease if quantity is greater than 1
    if (updatedItems[index].quantity > 1) {
      updatedItems[index].quantity -= 1;

      setEditFormData(prev => ({
        ...prev,
        inventoryItems: updatedItems
      }));
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();

    if (editFormData.inventoryItems.length === 0) {
      setError('Будь ласка, додайте хоча б один предмет до вашого бронювання.');
      return;
    }

    if (!editFormData.purpose) {
      setError('Будь ласка, вкажіть мету вашого бронювання.');
      return;
    }

    if (editFormData.pickupDatetime >= editFormData.returnDatetime) {
      setError('Час отримання має бути раніше часу повернення.');
      return;
    }

    setEditLoading(true);
    setError('');

    try {
      // If alternativePickup is not checked, ensure alternativePickupPerson is empty
      const dataToSubmit = {
        ...editFormData,
        pickupDatetime: format(editFormData.pickupDatetime, "yyyy-MM-dd'T'HH:mm:ss"),
        returnDatetime: format(editFormData.returnDatetime, "yyyy-MM-dd'T'HH:mm:ss"),
        alternativePickupPerson: alternativePickup ? editFormData.alternativePickupPerson : ''
      };

      await bookingAPI.updateBooking(bookingToEdit.id, dataToSubmit);

      // Update the booking in the local state
      const updatedBookings = bookings.map(booking => 
        booking.id === bookingToEdit.id 
          ? {
              ...booking,
              pickupDatetime: format(editFormData.pickupDatetime, "yyyy-MM-dd'T'HH:mm:ss"),
              returnDatetime: format(editFormData.returnDatetime, "yyyy-MM-dd'T'HH:mm:ss"),
              purpose: editFormData.purpose,
              additionalNotes: editFormData.additionalNotes,
              alternativePickupPerson: alternativePickup ? editFormData.alternativePickupPerson : '',
              inventoryItems: editFormData.inventoryItems.map(item => ({
                id: item.id,
                inventoryItemId: item.inventoryItemId,
                inventoryItemName: itemDetails[item.inventoryItemId]?.name || `Item #${item.inventoryItemId}`,
                quantity: item.quantity
              }))
            }
          : booking
      );

      setBookings(updatedBookings);
      setSnackbarMessage('Бронювання успішно оновлено');
      setSnackbarOpen(true);
      setEditDialogOpen(false);
      setBookingToEdit(null);
    } catch (err) {
      setError(
        err.response?.data?.message || 
        'Не вдалося оновити бронювання. Будь ласка, перевірте вашу інформацію та спробуйте ще раз.'
      );
    } finally {
      setEditLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'APPROVED':
        return 'success';
      case 'REJECTED':
        return 'error';
      case 'DELETED':
        return 'default';
      case 'PENDING':
      default:
        return 'warning';
    }
  };

  const formatDateTime = (dateTimeStr) => {
    try {
      return formatDateWithMonth(new Date(dateTimeStr));
    } catch (e) {
      return dateTimeStr;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container>
      <Box sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Мої бронювання
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
          <Button 
            variant="contained" 
            color="primary"
            onClick={() => navigate('/booking/create')}
          >
            Створити нове бронювання
          </Button>
        </Box>

        {bookings.length > 0 ? (
          <Grid container spacing={3}>
            {bookings.map((booking) => (
              <Grid item xs={12} md={6} key={booking.id}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h6" component="div">
                        {booking.purpose}
                      </Typography>
                        <Chip 
                          label={booking.status} 
                          color={getStatusColor(booking.status)} 
                          size="small" 
                        />
                    </Box>

                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      <strong>Отримання:</strong> {formatDateTime(booking.pickupDatetime)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      <strong>Повернення:</strong> {formatDateTime(booking.returnDatetime)}
                    </Typography>

                    {booking.additionalNotes && (
                      <Typography variant="body2" gutterBottom>
                        <strong>Примітки:</strong> {booking.additionalNotes}
                      </Typography>
                    )}

                    <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                      Предмети:
                    </Typography>

                    <Box component="ul" sx={{ pl: 2, m: 0 }}>
                      {booking.inventoryItems.map((item) => (
                        <Typography component="li" variant="body2" key={item.id}>
                          {item.inventoryItemName} (Qty: {item.quantity})
                        </Typography>
                      ))}
                    </Box>
                  </CardContent>

                  <CardActions>
                    {(() => {
                      const picked = !!(booking.isPickedUp ?? booking.pickedUp);
                      const returned = !!(booking.isReturned ?? booking.returned);
                      return !(picked || returned);
                    })() && (
                      <>
                        <Button 
                          size="small" 
                          variant="outlined"
                          color="primary"
                          onClick={() => handleDeleteClick(booking)}
                        >
                          Скасувати бронювання
                        </Button>
                        <Button 
                          size="small" 
                          variant="contained"
                          color="primary"
                          onClick={() => handleEditClick(booking)}
                        >
                          Редагувати бронювання
                        </Button>
                      </>
                    )}
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body1">
              У вас ще немає бронювань.
            </Typography>
            <Button 
              variant="contained" 
              color="primary"
              sx={{ mt: 2 }}
              onClick={() => navigate('/booking/create')}
            >
              Створити ваше перше бронювання
            </Button>
          </Paper>
        )}
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
      >
        <DialogTitle>Скасування бронювання</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Ви впевнені, що хочете скасувати це бронювання? Цю дію неможливо скасувати.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Ні, залишити</Button>
          <Button onClick={handleDeleteConfirm} color="error" autoFocus>
            Так, скасувати бронювання
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Booking Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={handleEditCancel}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          <Typography variant="h4" sx={{ mt: 2, mb: 0 }}>Редагування бронювання</Typography>
        </DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

          <Box component="form" onSubmit={handleEditSubmit} noValidate sx={{ mt: 2 }}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={uk}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <DateTimePicker
                    label="Дата і час отримання"
                    value={editFormData.pickupDatetime}
                    onChange={(newValue) => handleDateChange('pickupDatetime', newValue)}
                    sx={{ width: '100%' }}
                    format="dd MMM yyyy HH:mm"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <DateTimePicker
                    label="Дата і час повернення"
                    value={editFormData.returnDatetime}
                    onChange={(newValue) => handleDateChange('returnDatetime', newValue)}
                    sx={{ width: '100%' }}
                    format="dd MMM yyyy HH:mm"
                  />
                </Grid>
              </Grid>
            </LocalizationProvider>

            <TextField
              margin="normal"
              required
              fullWidth
              id="purpose"
              label="Мета бронювання"
              name="purpose"
              value={editFormData.purpose}
              onChange={handleInputChange}
            />

            <TextField
              margin="normal"
              fullWidth
              id="additionalNotes"
              label="Додаткові примітки"
              name="additionalNotes"
              multiline
              rows={3}
              value={editFormData.additionalNotes}
              onChange={handleInputChange}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={alternativePickup}
                  onChange={handleAlternativePickupChange}
                  color="primary"
                />
              }
              label="Хтось інший забере предмети"
            />

            <Collapse in={alternativePickup} timeout="auto" unmountOnExit>
              <TextField
                margin="normal"
                fullWidth
                id="alternativePickupPerson"
                label="Особа, яка забере предмети"
                name="alternativePickupPerson"
                value={editFormData.alternativePickupPerson}
                onChange={handleInputChange}
                helperText="Введіть повне ім'я особи, яка забере предмети"
              />
            </Collapse>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" gutterBottom>
              Вибрані предмети
            </Typography>

            <List>
              {editFormData.inventoryItems.map((item, index) => (
                <ListItem key={index}>
                  <ListItemText
                    primary={itemDetails[item.inventoryItemId]?.name || `Item #${item.inventoryItemId}`}
                    secondary={`Кількість: ${item.quantity}`}
                  />
                  <ListItemSecondaryAction>
                    <IconButton onClick={() => handleDecreaseQuantity(index)}>
                      <RemoveIcon />
                    </IconButton>
                    <IconButton onClick={() => handleIncreaseQuantity(index)}>
                      <AddIcon />
                    </IconButton>
                    <IconButton edge="end" onClick={() => handleRemoveItem(index)}>
                      <DeleteIcon color="primary" />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
              {editFormData.inventoryItems.length === 0 && (
                <ListItem>
                  <ListItemText primary="Немає вибраних предметів" />
                </ListItem>
              )}
            </List>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" gutterBottom>
              Додати предмети
            </Typography>

            {itemsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    fullWidth
                    label="Виберіть предмет"
                    value={selectedItemId}
                    onChange={(e) => setSelectedItemId(e.target.value)}
                    SelectProps={{
                      native: true,
                    }}
                  >
                    <option value=""></option>
                    {availableItems.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.name} (Available: {item.availableQuantity})
                      </option>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField
                    fullWidth
                    label="Кількість"
                    type="number"
                    InputProps={{ inputProps: { min: 1 } }}
                    value={selectedQuantity}
                    onChange={(e) => setSelectedQuantity(e.target.value)}
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleAddItem}
                    disabled={!selectedItemId}
                    fullWidth
                  >
                    Додати
                  </Button>
                </Grid>
              </Grid>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', px: 2, pb: 3 }}>
            <Button 
              variant="outlined"
              onClick={handleEditCancel}
            >
              Скасувати
            </Button>
            <Button 
              variant="contained"
              onClick={handleEditSubmit} 
              disabled={editLoading || editFormData.inventoryItems.length === 0}
            >
              {editLoading ? 'Оновлення...' : 'Оновити бронювання'}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
      />
    </Container>
  );
};

export default UserBookings;
